import { useState } from 'react'

/**
 * Carrier artwork for a two-letter IATA code.
 *
 * The same 608 logos and 613 icons the native apps ship, served as static SVGs
 * rather than bundled — only the handful a tour actually uses ever loads.
 *
 * `variant`:
 *  - `icon` — the square tail/roundel mark, for a card corner or a row.
 *  - `logo` — the full horizontal wordmark.
 */
export function AirlineLogo({
  code,
  variant = 'icon',
  size = 40,
  onDark = false,
  className,
}: {
  code: string | null | undefined
  variant?: 'icon' | 'logo'
  size?: number
  /**
   * Render the mark for a dark surface.
   *
   * The carrier artwork is drawn for white paper — Air New Zealand's koru is
   * `#231F20`, which is invisible on its own near-black card. The native app
   * reaches for a `*_icon_light` asset for exactly this case, but those files
   * don't exist in the repo, so it renders nothing.
   *
   * `brightness(0) invert(1)` forces any artwork to flat white. That would
   * wreck a multi-colour mark, but this only ever applies to the carriers that
   * have a dark card — Air NZ and Virgin — and both are single-colour.
   */
  onDark?: boolean
  className?: string
}) {
  const [failed, setFailed] = useState(false)
  const resolved = normaliseCarrier(code)

  if (!resolved || failed) {
    return <FallbackMark size={size} className={className} />
  }

  return (
    <img
      className={className}
      src={`/assets/airlines/${resolved}_${variant}.svg`}
      alt={`${resolved} logo`}
      onError={() => setFailed(true)}
      style={{
        height: size,
        width: variant === 'icon' ? size : 'auto',
        maxWidth: variant === 'logo' ? size * 4 : size,
        objectFit: 'contain',
        filter: onDark ? 'brightness(0) invert(1)' : undefined,
      }}
    />
  )
}

function FallbackMark({ size, className }: { size: number; className?: string }) {
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden
      style={{ opacity: 0.4 }}
    >
      <path
        d="M21 16v-2l-8-5V3.5a1.5 1.5 0 0 0-3 0V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5z"
        fill="currentColor"
      />
    </svg>
  )
}

/**
 * The carrier code the artwork is filed under.
 *
 * Records are inconsistent: older ones store the airline's *name* ("Air New
 * Zealand"), newer ones the code ("NZ"), and some only have it inside the
 * flight number ("NZ129"). This tries all three, then applies the one
 * hard-coded remap the native app carries: Jetstar Asia files under JQ, not
 * its own 3K.
 */
/**
 * Airline names, as a human types them.
 *
 * The code path below only ever accepted a two-character code or a flight
 * number, so a flight saved as "Air New Zealand" with no number rendered no
 * logo at all — which is most flights entered by hand, and every flight
 * imported from a receipt that quotes the carrier in words.
 */
const NAMED_CARRIERS: Record<string, string> = {
  "AIR NEW ZEALAND": "NZ",
  "AIR NZ": "NZ",
  "JETSTAR": "JQ",
  "JETSTAR AIRWAYS": "JQ",
  "QANTAS": "QF",
  "VIRGIN AUSTRALIA": "VA",
  "EMIRATES": "EK",
  "SINGAPORE AIRLINES": "SQ",
  "FIJI AIRWAYS": "FJ",
  "AIR CANADA": "AC",
  "UNITED": "UA",
  "UNITED AIRLINES": "UA",
  "AMERICAN AIRLINES": "AA",
  "DELTA": "DL",
  "BRITISH AIRWAYS": "BA",
  "CATHAY PACIFIC": "CX",
  "MALAYSIA AIRLINES": "MH",
  "CHINA AIRLINES": "CI",
  "KOREAN AIR": "KE",
  "LATAM": "LA",
  "AIRCALIN": "SB",
}

export function normaliseCarrier(value: string | null | undefined): string | null {
  if (!value) return null

  const trimmed = value.trim().toUpperCase()
  if (!trimmed) return null

  // A name, before anything else — "QANTAS" would otherwise fall through the
  // two-letter checks below and come back empty.
  const named = NAMED_CARRIERS[trimmed]
  if (named) return named

  // Already a bare code.
  if (/^[A-Z0-9]{2}$/.test(trimmed)) return remap(trimmed)

  // Leading letters of a flight number: "NZ129" → "NZ", "3K201" → "3K".
  const prefix = /^([A-Z0-9]{2})\d/.exec(trimmed)
  if (prefix) return remap(prefix[1])

  const letters = /^([A-Z]+)/.exec(trimmed)
  if (letters && letters[1].length === 2) return remap(letters[1])

  return null
}

function remap(code: string): string {
  return code === '3K' ? 'JQ' : code
}

/**
 * Airlines whose artwork is drawn for a dark card.
 *
 * The native flight card themes these two — Air NZ near-black, Virgin navy —
 * and their marks are near-black, so on any light surface they disappear. The
 * card gives them their own dark background instead of special-casing the mark.
 */
export function carrierTheme(code: string | null | undefined): {
  background: string
  text: string
  muted: string
} | null {
  const resolved = normaliseCarrier(code)
  if (resolved === 'NZ') {
    return { background: '#141414', text: '#ffffff', muted: 'rgba(255,255,255,0.8)' }
  }
  if (resolved === 'VA') {
    return { background: '#051E55', text: '#ffffff', muted: 'rgba(255,255,255,0.8)' }
  }
  return null
}
